/**
 * <ms-quick-add>: add-to-bag for one variant from any Maison section (snippets/ms-quick-add.liquid).
 * Without JS the form posts to /cart/add. With JS it adds through Horizon's cart events, the same way
 * product-form.js does, so the bag count, the cart items and the cart drawer (auto-open setting) all update.
 * A section that changes the variant writes the new id into input[name="id"].
 */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class MsQuickAdd extends HTMLElement {
  /** @type {AbortController | undefined} */
  #ac;
  #timers = new Set();

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('submit', this.#submit, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
  }

  #submit = async (/** @type {SubmitEvent} */ event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');

    if (!form.reportValidity()) { button.removeAttribute('aria-busy'); return; }
    const formData = new FormData(form);
    const variantSelect = form.querySelector('select[name="id"]');
    if (variantSelect instanceof HTMLSelectElement) {
      formData.set('quantity', variantSelect.selectedOptions[0]?.dataset.quantity || '1');
    }
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const lines = [];
    if (formData.has('id')) {
      lines.push({ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 });
    } else {
      for (const [name, value] of formData) {
        const match = name.match(/^items\[(\d+)\]\[id\]$/);
        if (match) lines.push({ merchandiseId: String(value), quantity: Number(formData.get(`items[${match[1]}][quantity]`)) || 1 });
      }
    }
    const id = lines.map((line) => line.merchandiseId).join('-');
    const quantity = lines.reduce((sum, line) => sum + line.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferred.promise,
      })
    );

    const cfg = fetchConfig('javascript', { body: formData });
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...cfg,
        headers: { ...cfg.headers, Accept: 'text/html' },
      }).then((r) => r.json());

      const cart = await this.#fetchCart();
      const detail = {
        items: cart.items,
        source: 'ms-quick-add',
        sourceId: id,
        itemCount: quantity,
        sections: response.status ? undefined : response.sections,
        didError: Boolean(response.status),
      };
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail });

      if (response.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );
        // Shopify's own error text can run to a full product name; the pill says it in the house voice
        this.#say(button, this.dataset.error || 'Could not add this strap');
      } else {
        this.#say(button, this.dataset.added || 'Added');
      }
    } catch (error) {
      deferred.reject(error);
      this.#say(button, this.dataset.error || 'Could not add this strap');
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  async #fetchCart() {
    const items = document.querySelector('cart-items-component');
    if (items) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (items).fetchCartData();
    }
    const r = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } });
    return r.json();
  }

  /** Briefly swaps the button text and announces it (the button keeps its own text otherwise). */
  #say(/** @type {Element} */ button, /** @type {string} */ text) {
    const live = this.querySelector('[data-ms-quick-add-status]');
    if (live) live.textContent = text;
    const label = button.querySelector('[data-ms-quick-add-label]');
    if (!label) return;
    const before = label.textContent;
    label.textContent = text;
    const timer = setTimeout(() => {
      this.#timers.delete(timer);
      // A section may have relabelled the button meanwhile (a variant switch to sold out): keep its text then.
      if (label.textContent === text) label.textContent = before;
      if (live) live.textContent = '';
    }, 2200);
    this.#timers.add(timer);
  }
}

if (!customElements.get('ms-quick-add')) customElements.define('ms-quick-add', MsQuickAdd);
